import { createSelector } from 'reselect';
import { findIndex, reduce, uniqBy } from 'lodash';
import Supercluster from 'supercluster';
import { getLocation } from '@sda/utils/location';
import {
  mapBoundsSelector, mapExtraHitsSelector, mapObjectSelector, mapSelectors,
  mapHoverIdSelector, mapZoomSelector,
} from '..';
import type { AlgoliaBeacon, GeolocHit } from '@sda/helpers/algolia.helpers';

export const MAX_ZOOM = 20;

export const getMapStatus = mapSelectors.selectStatus;
export const getMapHitById = mapSelectors.selectById;
export const getMapHits = mapSelectors.selectAll;
export const getMapExtraHits = mapExtraHitsSelector;
export const getMapHitTotal = mapSelectors.selectTotal;

export const getMapObject = mapObjectSelector;
export const getMapBounds = mapBoundsSelector;
export const getMapHoverId = mapHoverIdSelector;
export const getMapZoom = mapZoomSelector;

export const getMapLoaded = createSelector(
  mapBoundsSelector,
  (bounds) => !!bounds,
);

export const getMapCombinedHits = createSelector(
  getMapHits,
  getMapExtraHits,
  (hits, extraHits) => uniqBy([
    ...hits || [],
    ...extraHits || [],
  ], (hit) => hit.objectID).filter((hit) => hit._geoloc?.lat),
);

interface PointProperties {
  cluster: boolean;
  objectID: string;
  point_count?: number;
  profileId: string;
}

export const getFilteredMapHits = createSelector(
  mapSelectors.selectAll,
  mapBoundsSelector,
  mapZoomSelector,
  (hits, bounds, zoom): [(Supercluster.PointFeature<PointProperties> | Supercluster.ClusterFeature<PointProperties>)[], Supercluster] => {
    const filteredHits = hits.filter((hit) => hit._geoloc?.lat);

    const points = filteredHits.map((hit) => {
      const hitLocation = getLocation(hit.publicBeacon?.profileData?.fuzzyLatLng || hit._geoloc);

      return {
        type: 'Feature',
        properties: { cluster: false, objectID: hit.objectID, profileId: hit.publicBeacon?.profileId },
        geometry: {
          type: 'Point',
          coordinates: [
            hitLocation.lng,
            hitLocation.lat,
          ],
        },
      };
    }) as Supercluster.PointFeature<PointProperties>[];

    const supercluster = new Supercluster<PointProperties, PointProperties>({
      maxZoom: MAX_ZOOM,
      radius: 75,
    });

    supercluster.load(points);

    return [
      supercluster.getClusters(
        [bounds?.sw.lng, bounds?.sw.lat, bounds?.ne.lng, bounds?.ne.lat],
        zoom,
      ),
      supercluster,
    ];
  },
);

interface GroupedMapHit {
  profileId: string;
  hits: GeolocHit<AlgoliaBeacon>[];
}

export const getGroupedMapHits = createSelector(
  mapSelectors.selectAll,
  (hits) => reduce(hits, (results: GroupedMapHit[], hit) => {
    const exists = findIndex(results, (result) => result.profileId === hit.publicBeacon?.profileId);

    if (exists !== -1) {
      const newResults = results;

      newResults[exists] = {
        ...newResults[exists],
        hits: newResults[exists].hits.concat(hit),
      };

      return newResults;
    }

    return [
      ...results,
      {
        profileId: hit.publicBeacon?.profileId,
        hits: [hit],
      },
    ];
  }, []),
);

export const getHoveredClusterId = createSelector(
  getFilteredMapHits,
  getMapHoverId,
  ([hits, supercluster], hoverId) => {
    if (!hoverId) {
      return null;
    }

    let i = 0;

    for (; i < hits.length; i += 1) {
      const cluster = hits[i];

      const {
        cluster: isCluster,
        point_count: pointCount,
      } = cluster.properties;

      if (isCluster) {
        const leaves = supercluster.getLeaves(cluster.id as number, pointCount);
        const isActive = findIndex(leaves, (leaf) => leaf.properties.objectID === hoverId || leaf.properties.profileId === hoverId) !== -1;

        if (isActive) {
          return cluster.id;
        }
      }
    }

    return null;
  },
);
